import React, { useEffect, useRef } from 'react';

import { ConfigProvider, Form } from 'antd';
import classNames from 'classnames';
import { match } from 'ts-pattern';

import {
  AutoSave,
  Camera,
  Connection,
  Editor,
  Engraving,
  Experimental,
  General,
  Module,
  Path,
  Privacy,
  Reset,
} from '../categories';
import { SettingsCard } from '../shared';

import styles from './SettingsModal.module.scss';
import type { CommonSettingProps, SettingCategoryConfig } from './types';
import { SettingCategory } from './types';

const CATEGORIES_WITHOUT_CARD = new Set([
  SettingCategory.EDITOR,
  SettingCategory.GENERAL,
  SettingCategory.MODULE,
  SettingCategory.RESET,
]);

interface SettingsContentProps {
  category: SettingCategory;
  categoryConfig: SettingCategoryConfig | undefined;
  commonProps: CommonSettingProps;
  isMobile?: boolean;
}

const SettingsContent = ({
  category,
  categoryConfig,
  commonProps,
  isMobile = false,
}: SettingsContentProps): React.JSX.Element => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      // On mobile, the .content div has overflow:visible, so the actual scroll container
      // is the parent .mobile-settings-content. We need to scroll the correct container.
      const scrollTarget = isMobile ? contentRef.current?.parentElement : contentRef.current;

      scrollTarget?.scrollTo({ behavior: 'smooth', top: 0 });
    });
  }, [category, isMobile]);

  const {
    changeActiveLang,
    editingAutosaveConfig,
    onReset,
    setEditingAutosaveConfig,
    setWarnings,
    supportedLangs,
    unitInputProps,
    warnings,
  } = commonProps;

  const renderSection = (): React.ReactNode =>
    match(category)
      .with(SettingCategory.GENERAL, () => (
        <General changeActiveLang={changeActiveLang} supportedLangs={supportedLangs} wrapped />
      ))
      .with(SettingCategory.CONNECTION, () => <Connection />)
      .with(SettingCategory.AUTOSAVE, () => (
        <AutoSave
          editingAutosaveConfig={editingAutosaveConfig}
          setEditingAutosaveConfig={setEditingAutosaveConfig}
          setWarnings={setWarnings}
          warnings={warnings}
        />
      ))
      .with(SettingCategory.CAMERA, () => <Camera />)
      .with(SettingCategory.EDITOR, () => (
        <Editor subSectionTitleClass={styles['sub-section-title']} unitInputProps={unitInputProps} wrapped />
      ))
      .with(SettingCategory.ENGRAVING, () => <Engraving />)
      .with(SettingCategory.PATH, () => <Path unitInputProps={unitInputProps} />)
      .with(SettingCategory.MODULE, () => (
        <Module subSectionTitleClass={styles['sub-section-title']} unitInputProps={unitInputProps} wrapped />
      ))
      .with(SettingCategory.PRIVACY, () => <Privacy />)
      .with(SettingCategory.EXPERIMENTAL, () => <Experimental />)
      .with(SettingCategory.RESET, () => (onReset ? <Reset onReset={onReset} /> : null))
      .exhaustive();

  const skipOuterCard = CATEGORIES_WITHOUT_CARD.has(category);

  const formTheme = {
    components: { Form: { itemMarginBottom: isMobile ? 16 : 0, labelFontSize: 14 } },
  };

  return (
    <div className={classNames(styles.content, { [styles.mobile]: isMobile })} ref={contentRef}>
      {categoryConfig && <div className={styles['section-title']}>{categoryConfig.label}</div>}
      <ConfigProvider theme={formTheme}>
        <Form
          colon={false}
          labelAlign="left"
          labelWrap
          layout={isMobile ? 'vertical' : 'horizontal'}
          wrapperCol={isMobile ? undefined : { flex: 1 }}
        >
          {skipOuterCard ? renderSection() : <SettingsCard>{renderSection()}</SettingsCard>}
        </Form>
      </ConfigProvider>
    </div>
  );
};

export default SettingsContent;
